import { Geolocation } from '@capacitor/geolocation';
import { distance } from '@turf/distance';
import { point, Units } from '@turf/helpers';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { searchPlaceByText } from '../api/queries/searchPlaceByText';
import { fetchReverseGeocode } from '../api/queries/getReverseGeocode';
import { LocationFetchError, LocationOOBException } from '../shared/exceptions';
import { LocationType } from '../shared/location-type';
import { MapData } from '../shared/map-data';
import { Viewport } from '../shared/viewport';
import { type Point } from '../types';
import { openMapAddress } from '../shared/openMap';

const isInBoundingBox = (bounds: number[][], coordinates: [number, number]): boolean => {
  const [minLng, minLat] = bounds[0];
  const [maxLng, maxLat] = bounds[1];
  const [lng, lat] = coordinates;
  return lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat;
};

const computeDistance = (p1: Point, p2: Point, units: Units): number => {
  const from = point(p1.coordinates);
  const to = point(p2.coordinates);
  return distance(from, to, { units });
};

export class LocationSearchResult {
  constructor(
    public label: string,
    public referencePoint?: Point,
    public boundingBox?: number[][],
    public _placeLocation?: Point,
    public mapData?: MapData
  ) {}

  static fromMapData(mapData: MapData, referencePoint?: Point): LocationSearchResult {
    // All datasources are within the bounding box, so we don't need it here.
    return new LocationSearchResult(mapData.datasourceName, referencePoint, undefined, undefined, mapData);
  }

  static fromPlacesResult(
    displayName: string,
    location: Point,
    referencePoint?: Point,
    boundingBox?: number[][]
  ): LocationSearchResult {
    return new LocationSearchResult(displayName, referencePoint, boundingBox, location);
  }

  // higher score means better match
  get rankingScore(): number {
    // let score = this.isDatasourceSearchResult ? 10 : 1 - Number(this.isOutOfBounds);
    let score = 1 - Number(this.isOutOfBounds) + (this.isDatasourceSearchResult ? 0.01 : 0);
    if (this.referencePoint) {
      // Penalize locations further away from the reference point
      const EARTH_CIRCUMFERENCE_KM = 40_075.017;
      score += 1 - computeDistance(this.referencePoint, this.location, 'kilometers') / EARTH_CIRCUMFERENCE_KM;
    }
    return score;
  }

  get isDatasourceSearchResult(): boolean {
    return !!this.mapData;
  }

  get location(): Point {
    return this._placeLocation ?? this.mapData!.marker.location;
  }

  get isOutOfBounds(): boolean {
    return this.boundingBox ? !isInBoundingBox(this.boundingBox, this.location.coordinates) : false;
  }
}

export interface ReverseLookupLocationResult {
  displayName: string;
}

interface LocationContextType {
  // permission
  isLocationPermissionGranted: boolean | null;
  checkLocationPermission: () => Promise<boolean>;
  // location
  myLocation: Point | null;
  setMyLocation: (location: Point | null) => void;
  searchedLocation: Point | null;
  setSearchedLocation: (location: Point | null) => void;
  lastSelectedLocationType: LocationType | null;
  lastSelectedLocation: Point | null;
  fetchCurrentLocation: (bounds?: number[][]) => Promise<Point | null>;
  searchLocation: (query: string, mapData: MapData[], boundingBox?: number[][]) => Promise<LocationSearchResult[]>;
  reverseLookupLocation: (location: Point, resultTypeFilter?: string) => Promise<ReverseLookupLocationResult | null>;
  getStreetViewImageUrl: (location: Point) => string;
  computeDistance: (p1: Point, p2: Point, units: Units) => number;
  viewport: Viewport | null;
  setViewport: (viewport: Viewport | null) => void;
  updateSearchCache: (mapData: MapData[]) => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: ReactNode;
}

const normalizeTextForSearch = (text: string): string => {
  const pattern = /_|-|'|\//g; // remove common word separation characters
  return text.toLowerCase().replace(pattern, '');
};

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [myLocation, setMyLocation] = useState<Point | null>(null);
  const [searchedLocation, setSearchedLocation] = useState<Point | null>(null);
  const [lastSelectedLocationType, setLastSelectedLocationType] = useState<LocationType | null>(null);
  const [lastSelectedLocation, setLastSelectedLocation] = useState<Point | null>(null);
  const [isLocationPermissionGranted, setLocationPermissionGranted] = useState<boolean | null>(null);
  const [viewport, setViewport] = React.useState<Viewport | null>(null);
  const [datasourceNamesForSearch, setDatasourceNamesForSearch] = useState<string[] | null>(null);
  const queryClient = useQueryClient();

  const updateSearchCache = React.useCallback((mapData: MapData[]): void => {
    setDatasourceNamesForSearch(mapData.map((d) => normalizeTextForSearch(d.datasourceName)));
  }, []);

  const checkLocationPermission = async (): Promise<boolean> => {
    const permissions = await Geolocation.checkPermissions();
    if (permissions.location === 'granted') {
      setLocationPermissionGranted(true);
      return true;
    }
    setLocationPermissionGranted(false);
    return false;
  };

  const fetchCurrentLocation = async (bounds?: number[][]): Promise<Point> => {
    let coordinates: [number, number];
    try {
      const {
        coords: { longitude, latitude },
      } = await Geolocation.getCurrentPosition();
      coordinates = [longitude, latitude];
    } catch {
      throw new LocationFetchError();
    }
    if (bounds) {
      if (!isInBoundingBox(bounds, coordinates)) {
        throw new LocationOOBException();
      }
    }

    const currentLocation: Point = { type: 'Point', coordinates };
    setLastSelectedLocationType(LocationType.MyLocation);
    setMyLocation(currentLocation);
    return currentLocation;
  };

  useEffect(() => {
    if (myLocation) {
      setLastSelectedLocationType(LocationType.MyLocation);
      setLastSelectedLocation(myLocation);
    } else if (searchedLocation) {
      setLastSelectedLocationType(LocationType.SearchedLocation);
      setLastSelectedLocation(searchedLocation);
    } else {
      setLastSelectedLocationType(null);
      setLastSelectedLocation(null);
    }
  }, [myLocation]);

  useEffect(() => {
    if (searchedLocation) {
      setLastSelectedLocationType(LocationType.SearchedLocation);
      setLastSelectedLocation(searchedLocation);
    } else if (myLocation) {
      setLastSelectedLocationType(LocationType.MyLocation);
      setLastSelectedLocation(myLocation);
    } else {
      setLastSelectedLocationType(null);
      setLastSelectedLocation(null);
    }
  }, [searchedLocation]);

  const searchLocationsByQuery = async (
    query: string,
    limit: number,
    referencePoint?: Point,
    boundingBox?: number[][]
  ): Promise<LocationSearchResult[]> => {
    const response = await searchPlaceByText(queryClient, query, limit, referencePoint, boundingBox);
    return response.data.places.map(({ location, displayName, address }) => {
      const searchDisplayName = `${displayName} (${address})`;
      return LocationSearchResult.fromPlacesResult(searchDisplayName, location, referencePoint, boundingBox);
    });
  };

  const datasourceSearch = React.useCallback(
    (query: string, mapData: MapData[], referencePoint?: Point): LocationSearchResult[] => {
      const queryForMatching = normalizeTextForSearch(query);
      return (
        datasourceNamesForSearch
          ?.map<[string, number]>((name, index) => [name, index])
          .filter(([name]) => name.includes(queryForMatching))
          .map(([, index]) => LocationSearchResult.fromMapData(mapData[index], referencePoint)) ?? []
      );
    },
    [datasourceNamesForSearch]
  );

  const searchLocation = React.useCallback(
    async (
      query: string,
      mapData: MapData[],
      boundingBox?: number[][],
      limit: number = 5
    ): Promise<LocationSearchResult[]> => {
      const referencePoint: Point | undefined = viewport
        ? { type: 'Point', coordinates: [viewport.longitude, viewport.latitude] }
        : undefined;
      try {
        const datasourceResults = datasourceSearch(query, mapData, referencePoint);
        const placesResults = await searchLocationsByQuery(query, limit, referencePoint, boundingBox);
        const allResults: LocationSearchResult[] = [...datasourceResults, ...placesResults];

        // Sort results in descending order of score
        allResults.sort((a, b) => b.rankingScore - a.rankingScore);
        return allResults.slice(0, limit);
      } catch (error) {
        console.error('Error searching location:', error);
      }
      return [];
    },
    [datasourceSearch, viewport]
  );

  const reverseLookupLocation = React.useCallback(
    async (location: Point, resultTypeFilter?: string): Promise<ReverseLookupLocationResult | null> => {
      try {
        return await fetchReverseGeocode(queryClient, location, resultTypeFilter);
      } catch (error) {
        console.error('Error reverse lookup:', error);
      }
      return null;
    },
    [queryClient]
  );

  const getStreetViewImageUrl = (location: Point): string => {
    const baseUrl = `${openMapAddress}/api/v1/places/street-view`;
    const queryParams = {
      size: '400x225', // using 16:9 aspect ratio
      coordinates: `${location.coordinates[0]},${location.coordinates[1]}`,
    };

    const url = new URL(baseUrl);
    url.search = new URLSearchParams(queryParams).toString();
    return url.toString();
  };

  return (
    <LocationContext.Provider
      value={{
        isLocationPermissionGranted,
        checkLocationPermission,
        myLocation,
        setMyLocation,
        searchedLocation,
        setSearchedLocation,
        lastSelectedLocationType,
        lastSelectedLocation,
        fetchCurrentLocation,
        searchLocation,
        reverseLookupLocation,
        getStreetViewImageUrl,
        computeDistance,
        viewport,
        setViewport,
        updateSearchCache,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};
